import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { identify, track, resetAnalytics } from '@/utils/observability';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUser(response.data);
      if (response.data) {
        identify(response.data.id, {
          email: response.data.email,
          plan_type: response.data.plan_type,
          is_admin: Boolean(response.data.is_admin),
        });
      }
    } catch (error) {
      console.error('Error loading user:', error);
      // Only logout if it's an authentication error
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      email,
      password
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    if (userData) {
      identify(userData.id, { email: userData.email, plan_type: userData.plan_type, is_admin: Boolean(userData.is_admin) });
      track('user_logged_in', { plan_type: userData.plan_type });
    }
    return userData;
  };

  const register = async (email, password, full_name, crefito, promo_code) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      email,
      password,
      full_name,
      crefito,
      promo_code: promo_code || null
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    if (userData) {
      identify(userData.id, { email: userData.email, plan_type: userData.plan_type });
      track('user_registered', { plan_type: userData.plan_type, with_promo: Boolean(promo_code) });
    }
    return userData;
  };

  const logout = async () => {
    try {
      // Best-effort audit log call (backend records it) — ignore failures
      if (token) {
        await axios.post(`${API_URL}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (_) { /* noop */ }
    track('user_logged_out');
    resetAnalytics();
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = async (data) => {
    const response = await axios.put(`${API_URL}/auth/me`, data, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    setUser(response.data);
    return response.data;
  };

  const setAuthFromTokenResponse = (response) => {
    if (!response?.access_token || !response?.user) return;
    localStorage.setItem('token', response.access_token);
    setToken(response.access_token);
    setUser(response.user);
    identify(response.user.id, { email: response.user.email, plan_type: response.user.plan_type, is_admin: Boolean(response.user.is_admin) });
  };

  // AUTH-02 — re-pull /auth/me so callers (e.g. verification banner, the
  // "Já confirmei" CTA) can refresh `email_verified` without a full reload.
  const refreshUser = async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      return response.data;
    } catch (_err) {
      return null;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    setAuthFromTokenResponse
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
